"use client";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import shiftAssignmentService from "../../../services/shiftAssignmentService";
import Pagination from "../../Common/Pagination";

export function ShiftPlanningForm() {
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsTotal, setAssignmentsTotal] = useState(0);
  const [assignmentsPage, setAssignmentsPage] = useState(1);
  const [assignmentsPerPage, setAssignmentsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    user: "",
    shift: "",
    startDate: "",
    endDate: "",
  });
  const [userSearch, setUserSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, shiftsRes, assignRes] = await Promise.all([
        shiftAssignmentService.getUsers(),
        shiftAssignmentService.getShifts(),
        shiftAssignmentService.getAssignments({ page: assignmentsPage, limit: assignmentsPerPage }),
      ]);

      setUsers(Array.isArray(usersRes) ? usersRes : []);
      setShifts(Array.isArray(shiftsRes) ? shiftsRes : []);
      setAssignments(Array.isArray(assignRes?.items) ? assignRes.items : []);
      setAssignmentsTotal(Number(assignRes?.total || 0));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load data");
      console.error("Error loading shift planning data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [assignmentsPage, assignmentsPerPage]);

  const validateForm = () => {
    if (!formData.user || !formData.shift || !formData.startDate || !formData.endDate) {
      toast.error("Please fill all required fields");
      return false;
    }

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);

    if (start > end) {
      toast.error("Start date must be before end date");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await shiftAssignmentService.createAssignment({
        user: formData.user,
        shift: formData.shift,
        startDate: formData.startDate,
        endDate: formData.endDate,
        priority: 0,
      });

      toast.success("Shift assignment created successfully");
      handleReset();
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create assignment");
      console.error("Error creating assignment:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      user: "",
      shift: "",
      startDate: "",
      endDate: "",
    });
    setUserSearch("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this assignment?")) return;

    setDeletingId(id);
    try {
      await shiftAssignmentService.deleteAssignment(id);
      toast.success("Assignment deleted successfully");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete assignment");
      console.error("Error deleting assignment:", err);
    } finally {
      setDeletingId(null);
    }
  };

  const selectedUser = users.find((u) => u._id === formData.user);
  const selectedShift = shifts.find((s) => s._id === formData.shift);

  const filteredUsers = users.filter((u) =>
    `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-4 sm:p-6">
          <h2 className="mb-4 text-lg font-semibold sm:text-xl">
            Create Shift Assignment
          </h2>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="user" className="mb-2 block text-sm font-medium">
                  Select User *
                </Label>
                <Input
                  type="text"
                  placeholder="Search user by name..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="mb-2"
                  disabled={loading || submitting}
                />
                {userSearch && filteredUsers.length > 0 && (
                  <div className="border rounded-md bg-white shadow-sm max-h-40 overflow-y-auto">
                    {filteredUsers.map((u) => (
                      <button
                        key={u._id}
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, user: u._id }));
                          setUserSearch("");
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                      >
                        {u.firstName} {u.lastName}
                      </button>
                    ))}
                  </div>
                )}
                {selectedUser && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 text-sm">
                    Selected: {selectedUser.firstName} {selectedUser.lastName}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="shift" className="mb-2 block text-sm font-medium">
                  Select Shift *
                </Label>
                <Select
                  value={formData.shift}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, shift: value }))
                  }
                  disabled={loading || submitting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a shift" />
                  </SelectTrigger>
                  <SelectContent>
                    {shifts.map((shift) => (
                      <SelectItem key={shift._id} value={shift._id}>
                        {shift.shiftName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedShift && (
                  <div className="mt-2 text-xs text-gray-600">
                    {selectedShift.inTime} - {selectedShift.outTime}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="startDate" className="mb-2 block text-sm font-medium">
                  Start Date *
                </Label>
                <Input
                  type="date"
                  id="startDate"
                  value={formData.startDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                  disabled={loading || submitting}
                />
              </div>

              <div>
                <Label htmlFor="endDate" className="mb-2 block text-sm font-medium">
                  End Date *
                </Label>
                <Input
                  type="date"
                  id="endDate"
                  value={formData.endDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  disabled={loading || submitting}
                />
              </div>

            </div>

            <div className="flex gap-2 pt-4">
              <Button type="submit" disabled={loading || submitting}>
                {submitting ? "Creating..." : "Create Assignment"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={loading || submitting}
              >
                Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Existing Assignments Table */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold sm:text-xl">Active Assignments</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading assignments...</div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No shift assignments found
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Shift</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.map((assignment) => (
                    <TableRow key={assignment._id}>
                      <TableCell className="text-sm">
                        {assignment.user?.firstName} {assignment.user?.lastName}
                      </TableCell>
                      <TableCell className="text-sm">
                        {assignment.shift?.shiftName}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(assignment.startDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(assignment.endDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(assignment._id)}
                          disabled={deletingId === assignment._id}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <Pagination
            currentPage={assignmentsPage}
            totalItems={assignmentsTotal}
            itemsPerPage={assignmentsPerPage}
            onPageChange={setAssignmentsPage}
            onItemsPerPageChange={(v) => { setAssignmentsPerPage(v); setAssignmentsPage(1); }}
            currentPageCount={assignments.length}
            itemLabel="assignments"
          />
        </CardContent>
      </Card>
    </div>
  );
}
